import { LegalAttentionBanner } from './LegalAttentionBanner'
import { MembershipsNotices } from './MembershipsSection'
import { knownBrands, knownTags, withoutBrand, withoutTag } from './OfferBrandField'
import { HeaderRefreshButton } from '../ListSelection'
/**
 * Ofertas: lo que vendes.
 *
 * La cuenta de Stripe ya no vive aquí — se conecta una vez y no se vuelve a
 * tocar, así que es un ajuste y está en Configuración → Stripe. Lo que queda en
 * esta pantalla es el trabajo de todos los días: las ofertas.
 *
 * Del estado de la cuenta sólo queda un aviso arriba, que dice qué falta para
 * que alguien pueda pagar y enlaza a donde se arregla. Toda la tienda se monta
 * sin Stripe conectado —crear, escribir, poner precio— y eso no lo bloquea
 * nada; lo único que la cuenta cierra es PUBLICAR, que es cuando el botón de
 * compra empieza a existir para el que visita el sitio.
 */
import { useI18n } from '../../../lib/i18n'
import type { ProjectTheme } from '../../../lib/preview/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSectionResource } from '../../../hooks/useSectionResource'
import { useStripeConnect } from '../../../lib/stripeConnect'
import { useBookingsData } from './useBookingsData'
import { bookingError } from './bookingShared'
import { BookingServiceDetail } from './BookingServiceDetail'
import { BookingDetail } from './BookingDetail'
import { SectionRefresh } from '../../SectionRefresh'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { OfferDetail } from './OfferDetail'
import { OffersPanel } from './OffersPanel'
import { failureDetail, hasPaidOffer, publishBlockFor } from './offerShared'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { StripeConnectBanner } from './StripeConnectBanner'
import { CatalogPageButton, LEGACY_CATALOG_PAGE_RECORD } from './CatalogPageButton'
import { PlanUsageSummary } from '../PlanUsageSummary'
import { SITE_PLAN_LIMITS } from '../../../lib/planLimits'
import type { CatalogKind } from '../../../lib/api'
import {
  getStripeConnection,
  listOffers,
  type BillingPlan,
  type Offer,
} from '../../../lib/api'

/**
 * La ficha de un servicio reservable, dentro del catálogo.
 *
 * Abrir una oferta desde «Todas las ofertas» tiene que dar exactamente lo mismo
 * que abrirla desde su sección del menú. Con una descarga ya pasaba —es la
 * misma `OfferDetail` en los dos sitios— y con un servicio con horario no:
 * Cobros enseñaba una ficha de sólo lectura con un aviso de «esto se edita en
 * Reservas». Ahora monta la ficha DE VERDAD, la misma que Reservas, con su
 * asistente, su horario, sus recursos y sus citas.
 *
 * Va en su propio componente y no suelto en la sección por los hooks: así el
 * horario y las citas se piden únicamente cuando hay un servicio abierto, y no
 * cada vez que alguien entra al catálogo a mirar una descarga.
 *
 * Los datos vienen de `useBookingsData`, que es la misma fuente que usa
 * Reservas y comparte sus claves de caché: venir de una sección o de la otra no
 * repite ninguna petición, y las dos no pueden discrepar.
 */
function BookableOfferDetail({
  projectId, token, service, plan, canConnectStripe, siteOrigin, theme,
  onBack, onOpenPlans, onOpenPublish, onChange, onArchived,
}: {
  projectId: string
  token: string
  service: Offer
  plan: BillingPlan
  canConnectStripe: boolean
  siteOrigin: string
  theme?: ProjectTheme | undefined
  onBack: () => void
  onOpenPlans: () => void
  onOpenPublish?: (() => void) | undefined
  onChange: (offer: Offer) => void
  onArchived: (offerId: string) => void
}) {
  const { t } = useI18n()
  const data = useBookingsData(projectId, token)
  const stripe = useStripeConnect(projectId, token, bookingError)
  /* La cita abierta, dentro de esta misma pantalla.
     Su «Ver» mandaba a Citas, y eso rompía justo lo que se vino a arreglar:
     entras al catálogo, abres un servicio y de pronto estás en otra sección.
     `BookingDetail` ya es una ventana con su `onClose`, así que se abre encima
     y al cerrarla sigues donde estabas. */
  const [openBookingId, setOpenBookingId] = useState<string | null>(null)
  const openBooking = openBookingId
    ? data.bookings.find((row) => row.id === openBookingId) ?? null
    : null

  return (
    <>
    {openBooking ? (
      <BookingDetail
        projectId={projectId}
        token={token}
        booking={openBooking}
        {...(service.images[0] ? { image: service.images[0] } : {})}
        capacity={service.booking.capacity}
        /* Sin `onOpenService`: ya estás dentro de ese servicio, y el atajo
           llevaría a la pantalla en la que ya estás. */
        onClose={() => setOpenBookingId(null)}
        onChange={data.applyBooking}
      />
    ) : null}
    <BookingServiceDetail
      projectId={projectId}
      token={token}
      service={service}
      /* La misma cuenta que hace Reservas: el cupo de publicadas es del
         catálogo entero, así que una descarga le quita el hueco a un servicio. */
      publishBlock={publishBlockFor(service, {
        stripeReady: stripe.ready,
        canConnectStripe,
        plan,
        publishedOffers: data.offers.filter((row) => row.status === 'active').length,
      })}
      canConnectStripe={canConnectStripe}
      plan={plan}
      publishedOffers={data.offers.filter((row) => row.status === 'active').length}
      connectingStripe={stripe.connecting}
      onConnectStripe={() => { void stripe.connect() }}
      onOpenPlans={onOpenPlans}
      {...(onOpenPublish ? { onOpenPublish } : {})}
      bookings={data.bookings.filter((row) => row.offerId === service.id)}
      resources={data.resources}
      timezone={data.timezone}
      siteOrigin={siteOrigin}
      scheduleLoading={data.scheduleLoading}
      /* Lo único que cambia respecto a Reservas: a qué lista vuelve el botón.
         El título lo pone la propia ficha, y nombra al servicio en los dos
         sitios. */
      backLabel={t('offers.detail.back')}
      onBack={onBack}
      onChange={onChange}
      onDeleted={onArchived}
      onResourcesChange={data.setResources}
      onOpenBooking={setOpenBookingId}
      onBookingsChange={data.reloadBookings}
      {...(theme ? { theme } : {})}
    />
    </>
  )
}

export function PaymentsSection({ projectId, token, record, onRecordChange, onOpenStripeSettings, canConnectStripe, plan, projectTheme, onOpenPlans, onOpenBookingService, lockedKind, memberships = false, onOpenCatalog, onCreateBookingService, siteOrigin, onOpenPublish, onOpenContact, onOpenPolicies, autoCreate = false, onAutoCreateHandled }: {
  projectId: string
  token: string
  /**
   * Si el plan permite conectar Stripe. Ver `assertCanConnectStripe` en el
   * worker. Crear ofertas ya no depende del plan; cobrarlas sí, y publicarlas
   * va detrás de tener cuenta.
   */
  canConnectStripe: boolean
  /** El plan del proyecto: lo pide el medidor de ofertas publicadas. */
  plan: BillingPlan
  /** La paleta del editor, para la vista previa del índice público. */
  projectTheme?: ProjectTheme | undefined
  onOpenPlans: () => void
  /** La oferta abierta, tal y como viene de la URL. Sin ella, la lista. */
  record?: string | null | undefined
  onRecordChange?: ((record: string | null) => void) | undefined
  /** El aviso no conecta nada: lleva a donde se conecta. */
  onOpenStripeSettings: () => void
  /** A Políticas, desde el aviso de documentos sin publicar. */
  onOpenPolicies?: (() => void) | undefined
  /**
   * Abrir un servicio reservable en SU sección.
   *
   * Una reserva es una oferta y sale en este catálogo, pero se edita en
   * Reservas: allí está el asistente que sabe pintar la agenda. Aquí sólo se
   * pasa el camino; quién es quién lo decide la lista. */
  onOpenBookingService?: ((offerId: string) => void) | undefined
  /** Llegar con el alta ya pedida. Sólo es de paso; lo atiende la lista. */
  autoCreate?: boolean | undefined
  onAutoCreateHandled?: (() => void) | undefined
  /**
   * Con qué tipo llega acotada la sección. Ver `lockedKind` en la lista.
   *
   * `digital` es «Productos digitales» y `service` es «Servicios» — el mismo
   * catálogo mirando a una sola cosa. Las reservas no entran aquí: tienen su
   * propia sección y su propio asistente.
   */
  lockedKind?: 'digital' | 'physical' | 'service' | undefined
  /** Sólo las membresías y paquetes de clases (con `lockedKind: 'service'`). */
  memberships?: boolean | undefined
  /** Lleva al catálogo: crear una membresía sin ninguna oferta que incluir. */
  onOpenCatalog?: (() => void) | undefined
  /** El dominio público del sitio, o null si todavía no se ha publicado. */
  siteOrigin?: string | null | undefined
  /** Abre el editor: es donde se pone el botón de compra de una oferta. */
  /** Y el editor señalando Publicar, para el sitio que aún no lo está. */
  onOpenPublish?: (() => void) | undefined
  /** Abre la ficha de un comprador en Contactos: lo pide la tabla de ventas
   *  que la ficha de una oferta comparte con Cobros. */
  onOpenContact?: ((identity: string) => void) | undefined
  /** Empezar una reserva: se crea en Reservas, que es donde vive su asistente. */
  onCreateBookingService?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const connectionQuery = useSectionResource(token, [projectId, 'stripe-connection'], () => getStripeConnection(projectId, token))
  const offersQuery = useSectionResource(token, [projectId, 'offers'], () => listOffers(projectId, token))
  const connection = connectionQuery.data
  const offers = useMemo(() => offersQuery.data ?? [], [offersQuery.data])
  const setOffers = offersQuery.setData
  const loading = connectionQuery.loading
  const offersLoading = offersQuery.loading
  const failed = Boolean(connectionQuery.error)
  const offersFailed = Boolean(offersQuery.error)
  const offersError = offersQuery.error ? failureDetail(offersQuery.error) : ''
  const connectionError = connectionQuery.error ? failureDetail(connectionQuery.error) : ''
  const load = connectionQuery.refresh
  const loadOffers = offersQuery.refresh
  const offerLoader = useRecordLoader(offersLoading || loading || (Boolean(record) && !offers.some((offer) => offer.id === record) && offersQuery.refreshing))
  const refresh = () => { void load(); void loadOffers() }
  // These sidebar destinations reuse the same mounted component and catalogue.
  const previousKind = useRef(lockedKind)
  useEffect(() => {
    if (previousKind.current === lockedKind) return
    previousKind.current = lockedKind
    void load()
    void loadOffers()
  }, [lockedKind, load, loadOffers])


  const status = connection?.status ?? 'none'

  /** Abrir o cerrar una ficha es navegación, así que pasa por la URL. */
  const openOffer = (offerId: string | null) => { onRecordChange?.(offerId) }

  /*
   * Los ajustes del índice público de esta familia.
   *
   * Sólo cuando la sección viene acotada: «Todas las ofertas» mezcla las tres
   * familias y no hay UNA página que configurar desde ahí. Ver `CatalogPageSettings`.
   */
  const catalogKind: CatalogKind | null = memberships ? null : lockedKind === 'digital' ? 'digital' : lockedKind === 'physical' ? 'products' : lockedKind === 'service' ? 'services' : null
  /* `record=page`: el enlace viejo a la pantalla de «Página pública», que ya
     no existe (se diseña en el editor). Se limpia y queda la lista. */
  useEffect(() => {
    if (record !== LEGACY_CATALOG_PAGE_RECORD) return
    onRecordChange?.(null)
  }, [record, onRecordChange])

  const selectedOffer = record ? offers.find((offer) => offer.id === record) ?? null : null

  const backFromOffer = () => { openOffer(null) }

  /**
   * Un id que ya no corresponde a nada vuelve a la lista.
   *
   * Pasa con un enlace viejo, y también con la oferta que se acaba de retirar:
   * sin esto la pantalla se quedaba cargando para siempre una ficha que ya no
   * existe, sin decir por qué.
   */
  useEffect(() => {
    if (!record || record === LEGACY_CATALOG_PAGE_RECORD || offersQuery.refreshing || offersLoading || offersFailed) return
    if (!offers.some((offer) => offer.id === record)) onRecordChange?.(null)
  }, [record, offers, offersQuery.refreshing, offersLoading, offersFailed, onRecordChange])

  /*
   * Con una oferta en la URL, la ficha ocupa la pantalla entera: no es un panel
   * dentro de esta página, es otra página. Mientras la lista viaja, la cabecera
   * ya es la de la ficha —mismo título, mismo botón de vuelta— para que al
   * llegar los datos lo único que cambie sea el contenido, y no parezca que la
   * navegación se equivocó de sitio y se corrigió sola.
   */
  if (record) {
    /* El cargador manda mientras se despide, aunque la oferta ya haya llegado:
       se desvanece y entonces deja pasar la ficha. Ver `useRecordLoader`. */
    /* La espera sustituye a la ficha ENTERA, cabecera incluida: una cabecera
       sobre un vacío es una pantalla a medio hacer. Ver `SectionLoader`. */
    if (offerLoader.visible) return <SectionLoader loader={offerLoader} label={t('offers.loadingOne')} />
    if (selectedOffer) {
      /* Un servicio con horario abre la ficha de Reservas, no la de una oferta:
         es la misma cosa mirada desde otra lista, y verla distinta según por
         dónde se entró era justo el problema. Ver `BookableOfferDetail`. */
      if (selectedOffer.booking.durationMinutes !== null) {
        return (
          <div className="dash-loaded-content-in">
            <BookableOfferDetail
              projectId={projectId}
              token={token}
              service={selectedOffer}
              plan={plan}
              canConnectStripe={canConnectStripe}
              siteOrigin={siteOrigin ?? ''}
              {...(projectTheme ? { theme: projectTheme } : {})}
              onBack={backFromOffer}
              onOpenPlans={onOpenPlans}
              {...(onOpenPublish ? { onOpenPublish } : {})}
              onChange={(updated) => setOffers((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))}
              onArchived={(offerId) => {
                setOffers((rows) => rows.filter((row) => row.id !== offerId))
                backFromOffer()
              }}
            />
          </div>
        )
      }
      return (
        /* Entra desvanecido, no de golpe: la mascota acaba de irse
           desvaneciéndose y esto releva ese mismo gesto. */
        <div className="dash-loaded-content-in">
        <OfferDetail
          projectId={projectId}
          token={token}
          offer={selectedOffer}
          connectStatus={loading || failed ? null : status}
          canConnectStripe={canConnectStripe}
          plan={plan}
          publishedOffers={offers.filter((row) => row.status === 'active').length}
          brands={knownBrands(offers)}
          onBrandRemoved={(name) => setOffers((rows) => withoutBrand(rows, name))}
          tags={knownTags(offers)}
          onTagRemoved={(name) => setOffers((rows) => withoutTag(rows, name))}
          onOpenPlans={onOpenPlans}
          onBack={backFromOffer}
          onChange={(updated) => setOffers((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))}
          onArchived={() => {
            setOffers((rows) => rows.filter((row) => row.id !== selectedOffer.id))
            backFromOffer()
          }}
          onOpenStripeSettings={onOpenStripeSettings}
          {...(onOpenBookingService ? { onOpenBookingService } : {})}
          siteOrigin={siteOrigin ?? null}
          {...(onOpenPublish ? { onOpenPublish } : {})}
          {...(onOpenContact ? { onOpenContact } : {})}
          {...(projectTheme ? { theme: projectTheme } : {})}
        />
        </div>
      )
    }
    /* Un id que no corresponde a ninguna oferta: el efecto de arriba ya está
       devolviendo a la lista, y este fotograma es el que queda en medio. */
    return null
  }

  /* La espera sustituye a la pantalla ENTERA: ni el aviso de Stripe, ni el
     título, ni la lista. El cargador estaba dentro de `OffersPanel`, así que
     todo lo que vive por encima de la lista —que es lo que primero se lee al
     entrar— seguía pintándose sobre un hueco. Ver `SectionLoader`. */
  if (offerLoader.visible) return <SectionLoader loader={offerLoader} label={t('offers.loadingAll')} />

  return (
    <div className="dash-projects-view dash-pages-view dash-offers-list-view dash-loaded-content-in">
      <SectionRefresh refreshing={connectionQuery.refreshing || offersQuery.refreshing} failed={failed || offersFailed} />
      {/* El mismo aviso que sale encima de una ficha: un solo componente, para
          que no acaben diciendo lo mismo con palabras distintas. Va encima del
          título porque es contexto de toda la pantalla — lo primero que conviene
          saber al entrar es si esto que montas ya puede cobrar. */}
      <StripeConnectBanner
        overContent
        hasPaidOffer={offersQuery.data ? hasPaidOffer(offersQuery.data) : null}
        status={loading ? null : status}
        failed={!loading && failed}
        detail={connectionError}
        canConnect={canConnectStripe}
        onConnect={onOpenStripeSettings}
        onOpenPlans={onOpenPlans}
        onRetry={() => { void load() }}
      />

      {/* La cabecera dice dónde estás, no cómo se llama la lista por dentro.
          Acotada, la pantalla decía «Ofertas» aunque se hubiera entrado por
          «Productos digitales»: el menú prometía un sitio y la cabecera lo
          desmentía en el primer renglón. */}
      <DashboardPageHeader
        title={t(memberships ? 'memberships.title' : lockedKind === 'digital' ? 'offers.digital.sectionTitle'
          : lockedKind === 'physical' ? 'offers.physical.sectionTitle'
          : lockedKind === 'service' ? 'offers.service.sectionTitle'
          : 'offers.sectionTitle')}
        subtitle={t(memberships ? 'memberships.subtitle' : lockedKind === 'digital' ? 'offers.digital.sectionSubtitle'
          : lockedKind === 'physical' ? 'offers.physical.sectionSubtitle'
          : lockedKind === 'service' ? 'offers.service.sectionSubtitle'
          : 'offers.sectionSubtitle')}
        actionLayout="split"
        actions={(
          <>
            <HeaderRefreshButton refreshing={connectionQuery.refreshing || offersQuery.refreshing} onRefresh={refresh} />
            {/* La página que reúne esta lista, desde la lista: se diseña en el
                editor. Quien acaba de publicar una oferta y se pregunta dónde
                se ve está ya aquí. */}
            {catalogKind ? <CatalogPageButton projectId={projectId} kind={catalogKind} /> : null}
            {/* El cupo del plan, como en Páginas. Cuenta PUBLICADAS y de las
                tres familias juntas —una con agenda gasta el mismo hueco que un
                producto digital—, así que enseña el mismo número se entre por
                donde se entre. Acotada la lista, el número no: contar sólo lo
                filtrado diría que queda sitio donde no queda. */}
            <PlanUsageSummary
              items={[{
                label: t('usage.stat.offers'),
                used: offers.filter((offer) => offer.status === 'active').length,
                limit: SITE_PLAN_LIMITS[plan].offers,
              }]}
              plan={plan}
              onOpenPlans={onOpenPlans}
            />
          </>
        )}
      />

        {/* Vender sin términos ni reembolsos publicados: lo dice aquí, que es
            donde se vende. Ver `LegalAttentionBanner`. */}
        {onOpenPolicies && !record ? <LegalAttentionBanner projectId={projectId} token={token} onOpenPolicies={onOpenPolicies} /> : null}

        {/* Las ofertas son el trabajo de esta pantalla, así que van primero y no
            dependen de que la consulta de la cuenta haya salido bien: se pueden
            crear, editar y dejar listas sin Stripe de por medio. */}
        {memberships ? <MembershipsNotices projectId={projectId} token={token} onOpenPlans={onOpenPlans} /> : null}

        <OffersPanel
          memberships={memberships}
          {...(onOpenCatalog ? { onOpenCatalog } : {})}
          projectId={projectId}
          token={token}
          connectStatus={loading || failed ? null : status}
          canConnectStripe={canConnectStripe}
          plan={plan}
          offers={offers}
          failed={offersFailed && offersQuery.data === undefined}
          failureReason={offersError}
          onReload={() => { void loadOffers() }}
          onOpen={openOffer}
          {...(lockedKind ? { lockedKind } : {})}
          {...(onCreateBookingService ? { onCreateBookingService } : {})}
          autoCreate={autoCreate}
          {...(onAutoCreateHandled ? { onAutoCreateHandled } : {})}
          onOffersChange={setOffers}
          onOpenPlans={onOpenPlans}
          onOpenStripeSettings={onOpenStripeSettings}
        />
    </div>
  )
}
